<template>
  <div
    class="compact-light-card p-3 rounded-lg border transition-all duration-200 cursor-pointer"
    :class="{
      'border-primary bg-primary/10': isSelected,
      'border-border hover:border-primary/50': !isSelected
    }"
    @click="emit('click')"
  >
    <!-- Top line: Pip + Name + Effect Button (for WLED) -->
    <div class="flex items-center gap-2 mb-2">
      <div 
        class="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
        :style="{ backgroundColor: lightColor }"
      ></div>
      <span class="text-sm font-medium truncate flex-1">{{ light.name }}</span>
      
      <!-- WLED Effects Button -->
      <Popover v-if="isWledLight">
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            class="h-6 w-6 p-0"
            @click.stop
            title="WLED Effects"
          >
            <Sparkles class="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-80 p-4" align="start" side="left" :collision-padding="20">
          <div @click.stop>
            <WledEffectPanel
              :light-id="light.id"
              :scene-id="sceneId"
              @effect-applied="handleEffectApplied"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
    
    <!-- Bottom line: Brightness slider (0 = off) -->
    <div class="flex items-center gap-3">
      <Slider
        :model-value="[light.current_state?.on === false ? 0 : (light.current_state?.bri ?? 127)]"
        @update:model-value="handleBrightnessChange"
        @click.stop
        :max="255"
        :min="0"
        :step="1"
        class="flex-1"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sparkles } from 'lucide-vue-next'
import WledEffectPanel from './WledEffectPanel.vue'
import { hsvToRGB, hueStateToHSV, ctToRGB } from '@/lib/colorConversion'
import type { Light } from '@/stores/lights'

interface Props {
  light: Light
  isSelected: boolean
  sceneId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  click: []
  brightnessChange: [brightness: number]
}>()

// Check if this is a WLED light
const isWledLight = computed(() => !!props.light.wled_device_id)

// Calculate light color from state
const lightColor = computed(() => {
  const state = props.light.current_state
  
  if (state?.hue != null && state?.sat != null) {
    const hsv = hueStateToHSV(state.hue, state.sat)
    const rgb = hsvToRGB(hsv)
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  } else if (state?.ct != null) {
    const rgb = ctToRGB(state.ct)
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  }
  
  return '#888'
})

const handleBrightnessChange = (value: number[] | undefined) => {
  if (value && value[0] !== undefined) {
    emit('brightnessChange', value[0])
  }
}

const handleEffectApplied = () => {
  // Effect was applied - could show a toast or update UI
}
</script>

<style scoped>
.compact-light-card {
  background: hsl(var(--card));
}
</style>
